import { useForm } from "@inertiajs/react";

import FormModal from "@/components/Modal/FormModal";
import TextFieldComponent from "@/components/TextField";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode?: "create" | "edit";
  consumer?: Partial<ConsumerData>;
  onSaved?: (message: string) => void;
  onError?: (message: string) => void;
};

type ConsumerData = {
  id: number;
  name: string;
  email: string;
  phone: string;
  cpf: string;
  nickname: string;
  notification_email: string;
  discount_percentage: number;
};

type FormOptions = NonNullable<Parameters<ReturnType<typeof useForm>["post"]>[1]>;

export default function CreateConsumerModal({
  isOpen,
  onClose,
  mode = "create",
  consumer,
  onSaved,
  onError,
}: Props) {
  const isEdit = mode === "edit";
  const form = useForm({
    name: consumer?.name || "",
    nickname: consumer?.nickname || "",
    email: consumer?.email || "",
    cpf: consumer?.cpf || "",
    notification_email: consumer?.notification_email || "",
    discount: (consumer?.discount_percentage ?? 0.1) * 100,
  });

  const submit = () => {
    if (
      !Number.isFinite(form.data.discount) ||
      form.data.discount < 0 ||
      form.data.discount > 100
    ) {
      form.setError("discount", "Informe um desconto entre 0 e 100.");
      return;
    }

    form.clearErrors("discount");
    const onRequestError = (errors: Record<string, string | string[]>) => {
      const message = Object.values(errors).flat().join(" ");
      onError?.(message || "Não foi possível salvar o cliente.");
    };
    const onSuccess: NonNullable<FormOptions["onSuccess"]> = () => {
      form.reset();
      onClose();
      onSaved?.("Cliente atualizado com sucesso.");
    };

    if (isEdit && consumer?.id) {
      form.patch(`/manager/consumers/${consumer.id}`, { onSuccess, onError: onRequestError });
    } else {
      form.post("/manager/consumers", { onError: onRequestError });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Editar consumidor" : "Adicionar consumidor"}
      description="Cadastre os dados do consumidor e suas unidades consumidoras"
      submitButtonTitle={isEdit ? "Salvar alterações" : "Cadastrar consumidor"}
      onSubmit={submit}
      isSubmitting={form.processing}
      size="sm"
    >
      <div className="grid grid-cols-1 gap-4">
        <TextFieldComponent
          labelPosition="top"
          label="Nome completo"
          required
          type="text"
          placeholder="Digite o nome do usuário"
          value={form.data.name}
          onChange={(event) => form.setData("name", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="E-mail"
          required
          type="email"
          placeholder="E-mail no qual o usuário vai receber o convite"
          value={form.data.email}
          onChange={(event) => form.setData("email", event.target.value)}
          footnote="E-mail no qual o usuário vai receber o convite"
        />

        <TextFieldComponent
          labelPosition="top"
          label="CPF/CNPJ"
          required
          type="text"
          mask="cpf-cnpj"
          placeholder="000.000.000-00 ou 00.000.000/0000-00"
          value={form.data.cpf}
          onChange={(event) => form.setData("cpf", event.target.value)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Desconto (%)"
          required
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={form.data.discount}
          error={Boolean(form.errors.discount)}
          helpText={form.errors.discount}
          onChange={(event) => form.setData("discount", event.target.valueAsNumber || 0)}
        />

        <TextFieldComponent
          labelPosition="top"
          label="Apelido"
          optional
          type="text"
          placeholder="Ex.: Fazenda Sol Nascente"
          value={form.data.nickname}
          onChange={(event) => form.setData("nickname", event.target.value)}
          footnote="Só será exibido para você"
        />

        <TextFieldComponent
          labelPosition="top"
          label="E-mail de notificação"
          optional
          type="email"
          placeholder="E-mail no qual o usuário vai receber o convite"
          value={form.data.notification_email}
          onChange={(event) => form.setData("notification_email", event.target.value)}
          footnote="E-mail alternativo para o recebimento de faturas"
        />
      </div>
    </FormModal>
  );
}
